import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { defer, EMPTY, filter, merge, Observable, of, Subject, switchMap, tap, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  concatWith,
  mergeMap,
  expand,
  exhaustMap,
  finalize,
  map,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AgentsService } from '@app/shared/services/agents.service';
import { AgentStreamEvent } from '@app/shared/services/agent-stream';
import { TurnProgressStatus, ViewResponse } from '@app/shared/models/agent-chat.models';
import { CANDIDATE_CHATBOT_SEARCH_PREFIX, NLS_SEARCH_PREFIX } from '@app/app-routing-paths';
import {
  agentTurnProgressReceived,
  startAgentSession,
  startAgentSessionSuccess,
  startAgentSessionError,
  continueAgentSession,
  continueAgentSessionSuccess,
  continueAgentSessionError,
  loadAgentSessionState,
  loadAgentSessionStateSuccess,
  loadAgentSessionStateError,
  executeStartAgentSession,
  sendAckSuccess,
  sendAckError,
  loadChatbotResults,
  loadChatbotResultsSuccess,
  loadChatbotResultsError,
  applyStagedChanges,
  applyStagedChangesError,
  discardStagedChanges,
  stagedChangesError,
  stagedChangesReconciled,
  stageCriteriaEdit,
  reviewAndSearchEmbedded,
  agentReplyDelta,
  agentReplyReset,
  agentReplyDone,
  agentTurnProgressLine,
  agentReplySplit,
} from './chatbot.actions';
import {
  getChatSessionState,
  getChatbotThreadID,
} from '@app/collections/chatbot/chatbot.selectors';
import { resetCandidatesState } from '@app/collections/candidates/candidates.actions';
import {
  CHATBOT_RESULTS_PAGE_SIZE,
  CHATBOT_RESULTS_MAX_FILL_TRIES,
  chatbotResponseToPagination,
  ChatbotCandidatesApiResponse,
  ChatbotCandidatesFillState,
} from '@app/shared/models/chatbot-candidate.mapper';
import { Action, Store } from '@ngrx/store';
import { REVIEW_AND_SEARCH_PROMPT } from '@app/modules/authenticated-view/pages/nls-search/chatbot/components/chat-interaction/chat-interaction.component';
import { getSelectedProjectId } from '@app/collections/projects/projects.selectors';

/** The ranked candidate ids of one turn, as a comparable signature (null when absent). */
const rankedSignature = (response: ViewResponse | null | undefined): string | null => {
  const ranked = response?.reranked_results;
  if (!Array.isArray(ranked)) {
    return null;
  }
  const ids = ranked
    .map((r) => (r as { candidate_id?: string } | null)?.candidate_id ?? '')
    .join(',');
  // A re-anchor can return the same ordered IDs with a different vector query, so fold the
  // anchor in — but stay null when neither is set, or the "no search yet" guard stops working.
  const anchor = response?.search_anchor;
  if (!ids && !anchor) {
    return null;
  }
  const anchorSignature = anchor ? `${anchor.candidate_id}:${anchor.query_text}` : '';
  return `${ids}|anchor:${anchorSignature}`;
};

@Injectable()
export class ChatbotEffects {
  private actions$ = inject(Actions);
  private agentsService = inject(AgentsService);
  private router = inject(Router);
  private store = inject(Store);
  private fillInFlight: string | null = null;

  /** Ranking of the last turn we saw; compared per turn to detect an in-place re-search. */
  private lastRankedSignature: string | null = null;

  private static readonly PROGRESS_POLL_DELAY_MS = 1000;
  private static readonly PROGRESS_POLL_INTERVAL_MS = 2000;

  private withProgress<T>(
    threadId: string,
    runId: string,
    turn$: Observable<T>,
  ): Observable<T | Action> {
    const turnDone$ = new Subject<void>();
    const progress$ = timer(
      ChatbotEffects.PROGRESS_POLL_DELAY_MS,
      ChatbotEffects.PROGRESS_POLL_INTERVAL_MS,
    ).pipe(
      switchMap(() =>
        this.agentsService.getProgress(threadId, runId).pipe(catchError(() => EMPTY)),
      ),
      filter((response) => response.status !== null),
      map((response) =>
        agentTurnProgressReceived({
          payload: {
            log: (response.log ?? [response.status as TurnProgressStatus]).map((s) => s.message),
            holds: (response.status as TurnProgressStatus).holds ?? [],
          },
        }),
      ),
      takeUntil(turnDone$),
    );

    // Progress first: its takeUntil must be subscribed before a fast turn can finalize.
    return merge(progress$, turn$.pipe(finalize(() => turnDone$.next())));
  }

  private streamedTurn(
    threadId: string,
    stream$: Observable<AgentStreamEvent>,
    onDone: (response: ViewResponse) => Action,
  ): Observable<Action> {
    return defer(() => {
      let settled = false;
      return stream$.pipe(
        mergeMap((event) => {
          switch (event.kind) {
            case 'delta':
              return of(agentReplyDelta({ payload: { text: event.text } }));
            case 'reset':
              return of(agentReplyReset());
            case 'reply_done':
              return of(agentReplyDone());
            case 'reply_split':
              return of(agentReplySplit({ payload: { leadIn: event.leadIn, ask: event.ask } }));
            case 'progress':
              return of(
                agentTurnProgressLine({ payload: { text: event.text, holds: event.holds } }),
              );
            case 'done':
              settled = true;
              return of(onDone(event.response));
            default:
              return EMPTY;
          }
        }),
        // A dropped stream (transport cut, BFF timeout) sends no `done` and no error frame,
        // so read the turn back from /state -- never re-POST it -- or loading never ends.
        concatWith(
          defer(() => (settled ? EMPTY : of(loadAgentSessionState({ payload: { threadId } })))),
        ),
        concatMap((action) => this.forCurrentThread(threadId, action)),
      );
    });
  }

  // Nothing cancels a stream the recruiter walked away from, so its late frames would land on
  // the thread now on screen: drop everything a superseded turn still has to say.
  private forCurrentThread(threadId: string, ...actions: Action[]): Observable<Action> {
    return this.store.select(getChatbotThreadID).pipe(
      take(1),
      concatMap((current) => (current === threadId ? of(...actions) : EMPTY)),
    );
  }

  // Every failed turn leaves the thread unrenderable: the start nulled `response`, and the
  // error clears only the flags. GET /state is idempotent, so read it back whatever cut it.
  private failedTurn(
    threadId: string,
    error: HttpErrorResponse,
    toErrorAction: (error: HttpErrorResponse) => Action,
  ): Observable<Action> {
    return this.forCurrentThread(
      threadId,
      toErrorAction(error),
      loadAgentSessionState({ payload: { threadId } }),
    );
  }

  private shouldRefillChatbotPage(state: ChatbotCandidatesFillState): boolean {
    return (
      state.response.hasMore &&
      state.response.content.length < CHATBOT_RESULTS_PAGE_SIZE &&
      state.tries < CHATBOT_RESULTS_MAX_FILL_TRIES
    );
  }

  private getSettledChatbotCandidatesPage(
    page: number,
    threadId: string,
    projectId: number,
  ): Observable<ChatbotCandidatesFillState> {
    return this.agentsService.getChatbotCandidates(page, threadId, projectId).pipe(
      map((response): ChatbotCandidatesFillState => ({
        response: response as ChatbotCandidatesApiResponse,
        previousLength: 0,
        tries: 1,
      })),
      expand((state): Observable<ChatbotCandidatesFillState> => {
        if (!this.shouldRefillChatbotPage(state)) {
          return EMPTY;
        }

        return this.agentsService.getChatbotCandidates(page, threadId, projectId).pipe(
          map((response): ChatbotCandidatesFillState => ({
            response: response as ChatbotCandidatesApiResponse,
            previousLength: state.response.content.length,
            tries: state.tries + 1,
          })),
        );
      }),
    );
  }

  private threadReset$ = this.actions$.pipe(
    ofType(loadAgentSessionState, executeStartAgentSession),
  );

  /** A new thread starts with no baseline, so the next turn always refreshes. */
  resetRankedSignature$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadAgentSessionState, executeStartAgentSession),
        tap(() => (this.lastRankedSignature = null)),
      ),
    { dispatch: false },
  );

  // Seed the baseline from a loaded session so a re-mounted dock does not read the first
  // answer of an unchanged ranking as a re-search and blank-reload the list.
  seedRankedSignature$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadAgentSessionStateSuccess),
        tap(({ payload }) => (this.lastRankedSignature = rankedSignature(payload.response))),
      ),
    { dispatch: false },
  );

  /**
   * Reload the shortlist whenever a turn moved the ranking.
   *
   * Post-results ANY turn can re-search now -- a typed go-ahead, "Review & Search" and
   * "Help me refine this" all apply and search with no panel and no approve resume -- so
   * refreshing only on the apply_staged / approve paths left the old candidates on screen
   * until a page reload. Every one of those paths ends in continueAgentSessionSuccess.
   */
  refreshResultsAfterResearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(continueAgentSessionSuccess),
      map(({ payload }) => rankedSignature(payload.response)),
      // Empty means no search has run (gathering), NOT a ranking that moved -- reloading there
      // fetches results for a thread that has none and strands the interview.
      filter((signature): signature is string => !!signature),
      filter((signature) => {
        const moved = signature !== this.lastRankedSignature;
        this.lastRankedSignature = signature;
        return moved;
      }),
      switchMap(() =>
        of(resetCandidatesState(), loadChatbotResults({ payload: { page: 0, restart: true } })),
      ),
    ),
  );

  showResultsWhenTheyArrive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(continueAgentSessionSuccess),
        filter(({ payload }) => !!(payload.response as ViewResponse).reranked_results?.length),
        withLatestFrom(this.store.select(getChatbotThreadID)),
        filter(([{ payload }, threadId]) => payload.threadId === threadId),
        filter(() => this.router.url.includes('/chat/')),
        tap(([{ payload }]) =>
          this.router.navigate([CANDIDATE_CHATBOT_SEARCH_PREFIX, payload.threadId]),
        ),
      ),
    { dispatch: false },
  );

  startAgentSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startAgentSession),
      map(({ payload }) => {
        const threadId = crypto.randomUUID();
        return executeStartAgentSession({
          payload: {
            prompt: payload.prompt,
            threadId,
            jdText: payload.jdText,
            interviewDepth: payload.interviewDepth,
          },
        });
      }),
    ),
  );

  executeStartAgentSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeStartAgentSession),
      tap(({ payload }) => this.router.navigate([NLS_SEARCH_PREFIX, 'chat', payload.threadId])),
      switchMap(({ payload }) => {
        const runId = crypto.randomUUID();
        const ack$ = this.agentsService.sendAck(payload.prompt, payload.threadId).pipe(
          map((res) => sendAckSuccess({ payload: { ack: res.ack } })),
          catchError((error: HttpErrorResponse) => of(sendAckError({ error }))),
        );

        const turn$ = defer(() =>
          this.streamedTurn(
            payload.threadId,
            this.agentsService.sendMessageTurnStream(
              payload.prompt,
              payload.threadId,
              runId,
              undefined,
              payload.jdText,
              payload.interviewDepth,
            ),
            (response) => startAgentSessionSuccess({ payload: { response } }),
          ).pipe(
            catchError((error: HttpErrorResponse) =>
              this.failedTurn(payload.threadId, error, (failure) =>
                startAgentSessionError({ error: failure }),
              ),
            ),
          ),
        );

        return merge(ack$, turn$);
      }),
    ),
  );

  continueAgentSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(continueAgentSession),
      filter(({ payload }) => payload.resumeConfig?.approved !== true),
      switchMap(({ payload }) => {
        const runId = crypto.randomUUID();
        const turn$ = defer(() =>
          this.streamedTurn(
            payload.threadId,
            payload.useMessageTurn && payload.prompt
              ? this.agentsService.sendMessageTurnStream(
                  payload.prompt,
                  payload.threadId,
                  runId,
                  payload.action,
                  undefined,
                  'regular',
                )
              : this.agentsService.resumeTurnStream(
                  payload.threadId,
                  runId,
                  payload.resumeConfig || {},
                  payload.action,
                  'regular',
                ),
            (response) =>
              continueAgentSessionSuccess({
                payload: { response, threadId: payload.threadId },
              }),
          ).pipe(
            catchError((error: HttpErrorResponse) =>
              this.failedTurn(payload.threadId, error, (failure) =>
                continueAgentSessionError({ error: failure }),
              ),
            ),
          ),
        );
        if (!payload.prompt) {
          return turn$;
        }

        const ack$ = this.agentsService.sendAck(payload.prompt, payload.threadId).pipe(
          map((res) => sendAckSuccess({ payload: { ack: res.ack } })),
          catchError((error: HttpErrorResponse) => of(sendAckError({ error }))),
        );

        return merge(ack$, turn$);
      }),
    ),
  );

  stagedWriteQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(continueAgentSession),
      concatMap((action) =>
        this.store.select(getChatSessionState).pipe(
          take(1),
          switchMap((chatState) => {
            if (chatState.threadId !== action.payload.threadId) {
              return EMPTY;
            }

            const { payload } = action;
            if (payload.resumeConfig?.approved !== true) {
              return EMPTY;
            }

            const runId = crypto.randomUUID();
            const turn$ = defer(() =>
              (payload.useMessageTurn && payload.prompt
                ? this.agentsService.sendMessageTurn(
                    payload.prompt,
                    payload.threadId,
                    payload.action,
                    undefined,
                    'regular',
                    runId,
                  )
                : this.agentsService.resumeTurn(
                    payload.threadId,
                    payload.resumeConfig || {},
                    payload.action,
                    'regular',
                    runId,
                  )
              ).pipe(
                map((response) =>
                  continueAgentSessionSuccess({
                    payload: { response: response as ViewResponse, threadId: payload.threadId },
                  }),
                ),
                catchError((error: HttpErrorResponse) =>
                  of(
                    continueAgentSessionError({ error }),
                    loadAgentSessionState({ payload: { threadId: payload.threadId } }),
                  ),
                ),
              ),
            );
            if (!payload.prompt) {
              return this.withProgress(payload.threadId, runId, turn$).pipe(
                takeUntil(this.threadReset$),
              );
            }

            const ack$ = this.agentsService.sendAck(payload.prompt, payload.threadId).pipe(
              map((res) => sendAckSuccess({ payload: { ack: res.ack } })),
              catchError((error: HttpErrorResponse) => of(sendAckError({ error }))),
            );
            return this.withProgress(payload.threadId, runId, merge(ack$, turn$)).pipe(
              takeUntil(this.threadReset$),
            );
          }),
        ),
      ),
    ),
  );

  updateStagedChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stageCriteriaEdit, discardStagedChanges),
      concatMap((action) => {
        const body =
          action.type === stageCriteriaEdit.type
            ? { staged_criteria: action.payload.criteria }
            : { requested_action: 'discard_staged' as const };

        return this.agentsService.patchSessionState(action.payload.threadId, body).pipe(
          switchMap(() => this.agentsService.getSessionState(action.payload.threadId)),
          map((response) => stagedChangesReconciled({ payload: { response } })),
          catchError((error: HttpErrorResponse) => of(stagedChangesError({ error }))),
          takeUntil(this.threadReset$),
        );
      }),
    ),
  );

  applyStagedChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applyStagedChanges),
      exhaustMap(({ payload }) => {
        const runId = crypto.randomUUID();
        const turn$ = this.agentsService.applyStagedChanges(payload.threadId, runId).pipe(
          // refreshResultsAfterResearch$ owns the reload -- dispatching it here too double-fetches.
          map((response) =>
            continueAgentSessionSuccess({ payload: { response, threadId: payload.threadId } }),
          ),
          catchError((error: HttpErrorResponse) => of(applyStagedChangesError({ error }))),
        );
        return this.withProgress(payload.threadId, runId, turn$).pipe(takeUntil(this.threadReset$));
      }),
    ),
  );

  reviewAndSearchEmbedded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reviewAndSearchEmbedded),
      switchMap(({ payload }) => {
        const runId = crypto.randomUUID();
        const turn$ = this.agentsService
          .sendMessageTurn(
            REVIEW_AND_SEARCH_PROMPT,
            payload.threadId,
            'review_and_search',
            undefined,
            'regular',
            runId,
          )
          .pipe(
            map((response) =>
              continueAgentSessionSuccess({
                payload: { response: response as ViewResponse, threadId: payload.threadId },
              }),
            ),
            catchError((error: HttpErrorResponse) => of(continueAgentSessionError({ error }))),
          );
        return this.withProgress(payload.threadId, runId, turn$);
      }),
    ),
  );

  loadAgentSessionState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAgentSessionState),
      switchMap(({ payload }) => {
        if (!payload.threadId) {
          return of(loadAgentSessionStateError({ error: new HttpErrorResponse({ status: 404 }) }));
        }

        return this.agentsService.getSessionState(payload.threadId).pipe(
          map((response) =>
            response
              ? loadAgentSessionStateSuccess({
                  payload: { response: response as ViewResponse, threadId: payload.threadId },
                })
              : loadAgentSessionStateError({ error: new HttpErrorResponse({ status: 404 }) }),
          ),
          catchError((error: HttpErrorResponse) => of(loadAgentSessionStateError({ error }))),
        );
      }),
    ),
  );
  loadCandidatesWhenChatbot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChatbotResults),
      withLatestFrom(
        this.store.select(getChatSessionState),
        this.store.select(getSelectedProjectId),
      ),
      filter(
        ([{ payload }, chatState, projectId]) =>
          !!payload.restart ||
          this.fillInFlight !== `${chatState.threadId}:${projectId}:${payload.page}`,
      ),
      switchMap(([{ payload }, chatState, projectId]) =>
        defer(() => {
          this.fillInFlight = `${chatState.threadId}:${projectId}:${payload.page}`;
          return this.getSettledChatbotCandidatesPage(payload.page, chatState.threadId!, projectId);
        }).pipe(
          map((fillState) => {
            return loadChatbotResultsSuccess({
              payload: {
                candidatesPage: chatbotResponseToPagination(
                  fillState.response,
                  this.shouldRefillChatbotPage(fillState),
                ),
              },
            });
          }),
          takeUntil(
            this.threadReset$.pipe(
              filter((reset) => reset.payload.threadId !== chatState.threadId),
            ),
          ),
          catchError((error: HttpErrorResponse) => of(loadChatbotResultsError({ error }))),
          finalize(() => (this.fillInFlight = null)),
        ),
      ),
    ),
  );
}
